// ---------------------------------------------------------------------------
// Reading and writing reviews, comments and replies.
//
// Reads go straight to Firestore (the rules allow visible posts, plus a
// person's own held ones). Every write goes through a callable in
// functions/reviews.js — the rules deny client writes — which is where the
// spam checks, counters and ratings live.
// ---------------------------------------------------------------------------

import {
  collection, collectionGroup, documentId, query, where, orderBy, limit, startAfter, getDocs, getDoc, doc,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions, BUSINESSES } from '../firebase';

export const PAGE_SIZE = 15;

export const TABS = [
  { id: 'top', label: 'Top' },
  { id: 'recent', label: 'Recent' },
  { id: 'restaurant', label: 'Owner' },
];

export const REPORT_REASONS = [
  { id: 'sexual-staff', label: 'Sexual comments about staff' },
  { id: 'harassment', label: 'Harassment or bullying' },
  { id: 'hate', label: 'Hate speech' },
  { id: 'personal-info', label: 'Personal information' },
  { id: 'spam', label: 'Spam or advertising' },
  { id: 'fake', label: 'Fake review' },
  { id: 'other', label: 'Something else' },
];

function postsRef(businessId) {
  return collection(db, BUSINESSES, businessId, 'posts');
}

// The feed query for one tab. Each combination has a composite index in
// firestore.indexes.json; `status == 'visible'` is also what the rules
// require of any list query.
export function feedQuery(businessId, tab, reviewsOnly, after) {
  const clauses = [where('status', '==', 'visible')];
  if (tab === 'restaurant') clauses.push(where('restaurantEngaged', '==', true));
  if (reviewsOnly) clauses.push(where('type', '==', 'review'));
  if (tab === 'top') clauses.push(orderBy('likeCount', 'desc'));
  clauses.push(orderBy('createdAt', 'desc'));
  if (after) clauses.push(startAfter(after));
  clauses.push(limit(PAGE_SIZE));
  return query(postsRef(businessId), ...clauses);
}

const toPost = (d) => ({ id: d.id, ...d.data() });

export async function fetchFeedPage(businessId, tab, reviewsOnly, after) {
  const snap = await getDocs(feedQuery(businessId, tab, reviewsOnly, after));
  return {
    posts: snap.docs.map(toPost),
    cursor: snap.docs[snap.docs.length - 1] || null,
    done: snap.docs.length < PAGE_SIZE,
  };
}

// Prompts from the Volcyy Team or the restaurant, pinned above every tab.
export async function fetchPinned(businessId) {
  const snap = await getDocs(query(
    postsRef(businessId),
    where('status', '==', 'visible'),
    where('pinned', '==', true),
    orderBy('createdAt', 'desc'),
    limit(3),
  ));
  return snap.docs.map(toPost);
}

// The viewer's own posts that are held for review. Only they can see these,
// and showing them (with a note) beats a comment that seems to vanish.
export async function fetchMyHeld(businessId, uid) {
  if (!uid) return [];
  const snap = await getDocs(query(postsRef(businessId), where('authorId', '==', uid), where('status', '==', 'held'), orderBy('createdAt', 'desc'), limit(PAGE_SIZE)));
  return snap.docs.map(toPost).filter((p) => p.status === 'held')
    .sort((a, b) => millis(b.createdAt) - millis(a.createdAt));
}

export async function fetchPost(businessId, postId) {
  const snap = await getDoc(doc(db, BUSINESSES, businessId, 'posts', postId));
  return snap.exists() ? toPost(snap) : null;
}

export async function fetchRepliesPage(businessId, postId, after = null) {
  const snap = await getDocs(query(
    collection(db, BUSINESSES, businessId, 'posts', postId, 'replies'),
    where('status', '==', 'visible'),
    orderBy('createdAt', 'asc'),
    ...(after ? [startAfter(after)] : []),
    limit(50),
  ));
  return { replies: snap.docs.map(toPost), cursor: snap.docs[snap.docs.length - 1] || null, done: snap.size < 50 };
}

// Which of these posts (or replies) the viewer has liked. One small read per
// item — the rules only let a person read their own like, so there is no
// "list my likes" query to batch this with.
export async function fetchLikedIds(pathsById, uid) {
  if (!uid) return new Set();
  const entries = Object.entries(pathsById), liked = new Set();
  for (let i = 0; i < entries.length; i += 30) {
    const chunk = entries.slice(i, i + 30);
    const references = chunk.map(([, path]) => doc(db, ...path, 'likes', uid));
    const snap = await getDocs(query(collectionGroup(db, 'likes'), where('uid', '==', uid), where(documentId(), 'in', references)));
    const found = new Set(snap.docs.map(d => d.ref.path));
    chunk.forEach(([id], j) => { if (found.has(references[j].path)) liked.add(id); });
  }
  return liked;
}

function callable(name) {
  const fn = httpsCallable(functions, name);
  return (data) => fn(data).then((r) => r.data);
}

export const createPost = callable('createPost');
export const createReply = callable('createReply');
export const toggleLike = callable('toggleLike');
export const reportContent = callable('reportContent');
export const deleteContent = callable('deleteContent');
// DEMO TOOLS (admin only, pre-launch)
export const setDemoLikes = callable('setDemoLikes');

// ---- pure helpers (tested in reviews.test.js) -----------------------------

export function millis(ts) {
  if (!ts) return 0;
  if (typeof ts.toMillis === 'function') return ts.toMillis();
  if (typeof ts.seconds === 'number') return ts.seconds * 1000;
  return Number(ts) || 0;
}

// "now", "5m", "3h", "2d", then "Mar 4" (and the year once it isn't this one).
export function timeAgo(ts, now = Date.now()) {
  const t = millis(ts);
  if (!t) return 'now';
  const s = Math.max(0, Math.floor((now - t) / 1000));
  if (s < 60) return 'now';
  if (s < 3600) return `${Math.floor(s / 60)}m`;
  if (s < 86400) return `${Math.floor(s / 3600)}h`;
  if (s < 7 * 86400) return `${Math.floor(s / 86400)}d`;
  const date = new Date(t);
  const sameYear = date.getFullYear() === new Date(now).getFullYear();
  return date.toLocaleDateString('en-US', sameYear
    ? { month: 'short', day: 'numeric' }
    : { month: 'short', day: 'numeric', year: 'numeric' });
}

// Mirrors publicName in functions/lib/handles.js, for the "Posting as …" line.
// The handle or nothing: a name worked out from the one they signed up with
// is exactly what the generated handles replaced, so it is not a fallback.
export function displayHandle(profile) {
  return profile?.handle ? String(profile.handle) : null;
}

export function compactCount(n) {
  const v = Number(n) || 0;
  if (v < 1000) return String(v);
  if (v < 10000) return `${(v / 1000).toFixed(1).replace(/\.0$/, '')}K`;
  if (v < 1000000) return `${Math.floor(v / 1000)}K`;
  return `${(v / 1000000).toFixed(1).replace(/\.0$/, '')}M`;
}

// The server's HttpsError messages are written for people; anything else
// (network, internal) gets a generic line.
export function friendlyCallError(err) {
  const code = String(err?.code || '');
  const message = String(err?.message || '').trim();
  // App Check turned the browser away — reCAPTCHA scored it below the
  // threshold, or no token was issued at all. That happens before any of our
  // code runs, so all Firebase sends back is a bare "Unauthenticated", which
  // tells the person nothing. Our own unauthenticated error (from
  // requireCaller in functions/reviews.js) has a real sentence in it, so the
  // bare word is what distinguishes the two.
  if (code === 'functions/unauthenticated' && /^unauthenticated\.?$/i.test(message)) {
    return "We couldn't verify your browser. Refresh the page and try again — if it keeps happening, try a different browser, or turn off your VPN or ad blocker.";
  }
  if (code.startsWith('functions/') && code !== 'functions/internal' && message) return message;
  return 'Something went wrong. Please try again.';
}
